import type { ReactNode } from 'react';

export interface LandscapeMetric {
    key: string;
    value: number | null;
    valueText?: string | null;
    label?: string | null;
    notes?: string | null;
}

export interface LandscapeData {
    title?: string | null;
    sheetName?: string | null;
    uvInstituteId?: string | null;
    placementWindow?: string | null;
    metrics: Record<string, LandscapeMetric>;
}

interface Props {
    landscape: LandscapeData;
    instituteName?: string | null;
    subtitle?: string | null;
}

/** Prototype line breaks — company_landscape_insights.html */
const PROTO_LINES: Record<string, [string, string]> = {
    total_alumni_companies: ['Total Unique Companies where', 'at least 1 alumni is present'],
    never_visited_with_alumni: ['At least 1 Alumni Present,', 'But Never Visited Campus'],
    alumni_present_and_visited: ['That have Alumni Presence and', 'Visited for Recruitment'],
    zero_alumni_recruited: ['Companies with Zero Alumni', 'Presence that Recruited'],
    total_unique_recruiters: ['Unique Companies that', 'Came for Placements'],
    high_influence_not_visited: [
        'Companies with Founder / Senior Management',
        "or Medium Level Influence that didn't Visit",
    ],
};

const DEFAULT_LABELS: Record<string, string> = Object.fromEntries(
    Object.entries(PROTO_LINES).map(([k, [a, b]]) => [k, `${a} ${b}`])
);

export const EMPTY_LANDSCAPE: LandscapeData = {
    title: '',
    metrics: Object.fromEntries(
        Object.entries(DEFAULT_LABELS).map(([key, label]) => [key, { key, value: 0, label }])
    ),
};

const formatCount = (n: number | null | undefined) => {
    const v = n == null || !Number.isFinite(Number(n)) ? 0 : Number(n);
    return v.toLocaleString('en-IN');
};

const metric = (landscape: LandscapeData, key: string): LandscapeMetric | null =>
    landscape?.metrics?.[key] || null;

/** Placement season label as in the prototype badge, e.g. 2026–27 (July start). */
export const currentPlacementSeason = (d = new Date()) => {
    const start = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
    return `${start}–${String((start + 1) % 100).padStart(2, '0')}`;
};

const CardLabel = ({
    metricKey,
    label,
    suffix,
}: {
    metricKey: string;
    label?: string | null;
    suffix?: string;
}) => {
    const proto = PROTO_LINES[metricKey];
    const custom = (label || '').trim();
    const useProto =
        !custom || custom.replace(/\s+/g, ' ') === (DEFAULT_LABELS[metricKey] || '').replace(/\s+/g, ' ');
    if (useProto && proto) {
        return (
            <>
                {proto[0]}
                <br />
                {proto[1]}
                {suffix ? ` ${suffix}` : ''}
            </>
        );
    }
    return (
        <>
            {custom}
            {suffix ? ` ${suffix}` : ''}
        </>
    );
};

const LandscapeCard = ({
    accent,
    value,
    metricKey,
    label,
    suffix,
    valueClassName = '',
}: {
    accent: 'primary' | 'info' | 'success' | 'warning';
    value: number | null | undefined;
    metricKey: string;
    label?: string | null;
    suffix?: string;
    valueClassName?: string;
}) => (
    <div className={`cl-landscape-card cl-landscape-card--${accent}`}>
        <div className={`display-5 font-700 mb-2 ${valueClassName}`.trim()}>{formatCount(value)}</div>
        <div className="cl-landscape-card-label">
            <CardLabel metricKey={metricKey} label={label} suffix={suffix} />
        </div>
    </div>
);

const BreakdownHint = ({ children }: { children: ReactNode }) => (
    <div className="cl-landscape-hint">
        <i className="bi bi-chevron-down text-muted" />
        <span>{children}</span>
    </div>
);

const CompanyLandscapeOverview = ({ landscape, instituteName, subtitle }: Props) => {
    const total = metric(landscape, 'total_alumni_companies');
    const neverVisited = metric(landscape, 'never_visited_with_alumni');
    const visitedAlumni = metric(landscape, 'alumni_present_and_visited');
    const zeroAlumni = metric(landscape, 'zero_alumni_recruited');
    const recruiters = metric(landscape, 'total_unique_recruiters');
    const highInfluence = metric(landscape, 'high_influence_not_visited');
    const windowLabel = landscape.placementWindow || metric(landscape, 'placement_window')?.valueText || '';

    const titleInstitute = String(landscape.title || '')
        .replace(/\s*[—–-]\s*Company Landscape Overview.*/i, '')
        .replace(/Company Landscape Overview/i, '')
        .trim();
    const badgeInstitute = (instituteName || titleInstitute || '').trim();
    const badgeText = [badgeInstitute || null, `Season ${currentPlacementSeason()}`].filter(Boolean).join(' · ');
    const neverVisitedSuffix = windowLabel ? `(${windowLabel})` : '';

    return (
        <div className="cl-landscape max-1250 mx-auto">
            <div className="cl-landscape-banner">
                <h1 className="font-20 font-700 text-white mb-0 d-inline-block align-middle me-2">
                    Placement Insights
                </h1>
                <span className="cl-landscape-badge">{badgeText}</span>
            </div>

            {subtitle ? <div className="font-12 text-secondary mb-3">{subtitle}</div> : null}

            <div className="cl-landscape-section-title">
                <h2 className="font-18 font-700 text-dark mb-0">Company Landscape Overview</h2>
            </div>

            <div className="row g-3">
                <div className="col-12 col-md">
                    <div className="row mb-4">
                        <div className="col-12 mb-4">
                            <LandscapeCard
                                accent="primary"
                                value={total?.value}
                                metricKey="total_alumni_companies"
                                label={total?.label}
                            />
                        </div>
                        <div className="col-12 mb-2">
                            <BreakdownHint>breaks down into</BreakdownHint>
                        </div>
                        <div className="col-12 col-sm-6 mb-3">
                            <LandscapeCard
                                accent="primary"
                                value={neverVisited?.value}
                                metricKey="never_visited_with_alumni"
                                label={neverVisited?.label}
                                suffix={neverVisitedSuffix}
                            />
                        </div>
                        <div className="col-12 col-sm-6 mb-3">
                            <LandscapeCard
                                accent="info"
                                value={visitedAlumni?.value}
                                metricKey="alumni_present_and_visited"
                                label={visitedAlumni?.label}
                            />
                        </div>
                        <div className="col-12 mb-2">
                            <BreakdownHint>from the {formatCount(neverVisited?.value)} untapped</BreakdownHint>
                        </div>
                        <div className="col-12 col-sm-6">
                            <LandscapeCard
                                accent="success"
                                value={highInfluence?.value}
                                metricKey="high_influence_not_visited"
                                label={highInfluence?.label}
                                valueClassName="cl-landscape-value-info"
                            />
                        </div>
                    </div>
                </div>

                <div className="col-md-auto">
                    <div className="cl-landscape-divider-vr d-none d-md-block" />
                    <div className="cl-landscape-divider-hr d-block d-md-none mb-3" />
                </div>

                <div className="col-12 col-md">
                    <div className="row mb-4">
                        <div className="col-12 mb-4">
                            <LandscapeCard
                                accent="warning"
                                value={recruiters?.value}
                                metricKey="total_unique_recruiters"
                                label={recruiters?.label}
                            />
                        </div>
                        <div className="col-12 mb-2">
                            <BreakdownHint>breaks down into</BreakdownHint>
                        </div>
                        <div className="col-12 col-sm-6">
                            <LandscapeCard
                                accent="info"
                                value={visitedAlumni?.value}
                                metricKey="alumni_present_and_visited"
                                label={visitedAlumni?.label}
                            />
                        </div>
                        <div className="col-12 col-sm-6">
                            <LandscapeCard
                                accent="success"
                                value={zeroAlumni?.value}
                                metricKey="zero_alumni_recruited"
                                label={zeroAlumni?.label}
                                valueClassName="text-success"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="cl-landscape-relate">
                <div className="font-15 font-700 text-dark mb-2">How these numbers relate:</div>
                <div className="lh-lg">
                    <span className="cl-landscape-num">{formatCount(total?.value)}</span>
                    {' = '}
                    <span className="cl-landscape-num">{formatCount(neverVisited?.value)}</span>
                    {' (never visited, with alumni) + '}
                    <span className="cl-landscape-num">{formatCount(visitedAlumni?.value)}</span>
                    {' (alumni present & visited)'}
                    <br />
                    <span className="cl-landscape-num">{formatCount(recruiters?.value)}</span>
                    {' = '}
                    <span className="cl-landscape-num">{formatCount(visitedAlumni?.value)}</span>
                    {' (alumni present & visited) + '}
                    <span className="cl-landscape-num">{formatCount(zeroAlumni?.value)}</span>
                    {' (zero alumni, still recruited)'}
                    <br />
                    <span className="cl-landscape-num">{formatCount(highInfluence?.value)}</span>
                    {' is a subset of '}
                    <span className="cl-landscape-num">{formatCount(neverVisited?.value)}</span>
                    {' — filtered to High / Medium influence companies only (primary outreach targets)'}
                </div>
            </div>
        </div>
    );
};

export default CompanyLandscapeOverview;
